<template>
	<div class="communicate-diagnosis">
		<template v-if="device">
			<div class="device-info">
				<gc-header :header-data="headerData">
					<template #status v-if="isDefaultDiagnosis && detail.deviceStatus !== undefined">
						<span :class="['status', status.class]">{{ status.text }}</span>
					</template>
				</gc-header>
			</div>
			<div class="modal-container">
				<div v-if="streamError" class="diagnosis-error">{{ streamError }}</div>
				<div v-if="!hasDetail" class="diagnosis-wait-panel">
					<i :class="streamActive ? 'el-icon-loading' : 'el-icon-warning-outline'"></i>
					<span>{{ streamActive ? '正在获取规则诊断结果...' : '未获取到规则诊断结果' }}</span>
				</div>
				<template v-else-if="isDefaultDiagnosis">
					<el-row :gutter="24" class="diagnosis-top-row">
						<el-col :span="13">
							<DeviceBasicInfo :deviceBasicInfo="deviceBasicInfo" />
						</el-col>
						<el-col :span="11">
							<DiagnosisAnalyze :detail="detail" />
						</el-col>
					</el-row>
					<AiDiagnosis
						v-if="showAiPanel"
						:result="aiResult"
						:running="streamActive"
						:not-required="aiNotRequired"
					/>
					<el-row :gutter="24">
						<el-col :span="13">
							<RunStatus :detail="detail" />
							<AlarmRecords :detail="detail" />
						</el-col>
						<el-col :span="11">
							<ProcessNode :detail="detail" />
						</el-col>
					</el-row>
				</template>
				<template v-else>
					<el-row :gutter="24" class="diagnosis-top-row compact">
						<el-col :span="12">
							<DeviceBasicInfo :deviceBasicInfo="deviceBasicInfo" />
						</el-col>
						<el-col :span="12">
							<DiagnosisAnalyze isAnalyzeDetails :detail="detail" resultKey="communicateResult" />
						</el-col>
					</el-row>
					<AiDiagnosis
						v-if="showAiPanel"
						:result="aiResult"
						:running="streamActive"
						:not-required="aiNotRequired"
					/>
					<CommunicateDetails
						:key="`${diagnosisMode}-${detail.deviceNo}-${detail.lastCommunicateTime}`"
						:detail="detail"
						:diagnosis-mode="diagnosisMode"
					/>
				</template>
			</div>
		</template>

		<GcEmpty v-else page-type="communicateDiagnosis" />
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { diagnosisModeLabels } from '@/store/modules/diagnosis'
import { nameTrans } from '@/utils'
import EventBus from '@/utils/eventBus'
import DeviceBasicInfo from './components/DeviceBasicInfo.vue'
import DiagnosisAnalyze from './components/DiagnosisAnalyze.vue'
import AiDiagnosis from './components/AiDiagnosis.vue'
import CommunicateDetails from './components/CommunicateDetails.vue'
import RunStatus from './components/RunStatus.vue'
import ProcessNode from './components/ProcessNode.vue'
import AlarmRecords from './components/AlarmRecords.vue'
export default {
	components: {
		DeviceBasicInfo,
		DiagnosisAnalyze,
		AiDiagnosis,
		CommunicateDetails,
		RunStatus,
		ProcessNode,
		AlarmRecords,
	},
	data() {
		return {
			diagnosisModeLabels,
		}
	},
	computed: {
		...mapGetters({
			userInfo: 'userInfo',
			dataList: 'dataList',
		}),
		...mapState('diagnosis', ['device', 'diagnosisMode', 'detail', 'aiResult', 'streamError']),
		...mapGetters('diagnosis', ['hasDetail', 'streamActive', 'aiNotRequired']),
		isDefaultDiagnosis() {
			return this.diagnosisMode === 'DEFAULT'
		},
		showAiPanel() {
			return this.hasDetail && (this.streamActive || this.aiResult || this.aiNotRequired)
		},
		headerData() {
			let title =
				this.detail.deviceTypeName || this.device.tenantName || this.diagnosisModeLabels[this.diagnosisMode]
			if (this.isDefaultDiagnosis && this.userInfo.tenantType == 1) {
				title = nameTrans(this.detail.userType, this.dataList.userType)
			}
			return {
				pic: 'account-detail',
				title,
				desc: [
					{
						icon: 'icon-bianhao',
						text: this.detail.deviceNo || this.device.deviceNo,
					},
					{
						icon: 'icon-daohangdizhi',
						text: this.detail.addressName,
						maxWidth: '40%',
					},
				],
			}
		},
		status() {
			const map = {
				0: {
					text: '待安装',
					class: 'wait-installed',
				},
				1: {
					text: '已安装',
					class: 'installed',
				},
				2: {
					text: '已拆除',
					class: 'had-uninstalled',
				},
			}
			return {
				text: map[this.detail.deviceStatus]?.text || '',
				class: map[this.detail.deviceStatus]?.class || '',
			}
		},
		deviceBasicInfo() {
			if (!this.isDefaultDiagnosis) {
				const modeInfo = ['CLOUD_LINK', 'LOCAL_LINK'].includes(this.diagnosisMode)
					? [{ key: 'ips', label: '通讯IP', value: this.displayIps(this.detail.ips) }]
					: [
							{ key: 'destIp', label: '目标IP', value: this.detail.destIp },
							{ key: 'destPort', label: '目标端口', value: this.detail.destPort },
					  ]
				return {
					title: '设备信息',
					row: 2,
					labelWidth: 80,
					list: [
						{
							key: 'deviceNo',
							label: '通讯编号',
							value: this.detail.deviceNo || this.device.deviceNo,
						},
						...modeInfo,
						{ key: 'isp', label: '运营商', value: nameTrans(this.detail.isp, this.dataList.isp) },
						{ key: 'lastCommunicateTime', label: '通讯时间', value: this.detail.lastCommunicateTime },
						{
							key: 'deviceGroupName',
							label: '设备组名称',
							value: this.detail.deviceGroupName,
							hidden: this.diagnosisMode === 'NB_CONVERT',
						},
					].filter(item => !item.hidden),
				}
			}
			return {
				title: '设备信息',
				row: 2,
				labelWidth: 80,
				list: [
					{
						key: 'deviceNo',
						label: '通讯编号',
						value: this.detail.deviceNo,
					},
					{
						key: 'deviceTypeName',
						label: '设备型号',
						value: this.detail.deviceTypeName,
					},
					{
						key: 'protocolNo',
						label: '协议',
						value: this.detail.protocolNo,
					},
					{
						key: 'communicationMode',
						label: '通讯方式',
						value: nameTrans(this.detail.communicationMode, this.dataList.communicationMode),
					},
					{
						key: 'deviceGroupName',
						label: '设备组名称',
						value: this.detail.deviceGroupName,
						hidden: ![1, 3].includes(this.detail.communicationMode),
					},
					{
						key: 'isp',
						label: '运营商',
						value: nameTrans(this.detail.isp, this.dataList.isp),
					},
					{
						key: 'tenantName',
						label: '所属租户',
						value: this.detail.tenantName,
					},
					{
						key: 'bizSysType',
						label: '业务系统',
						value: nameTrans(this.detail.bizSysType, this.dataList.bizSysType),
					},
					{
						key: 'deviceStatus',
						label: '安装状态',
						value: nameTrans(this.detail.deviceStatus, this.dataList.deviceStatus),
					},
					{
						key: 'createTime',
						label: '注册时间',
						value: this.detail.createTime,
					},
					{
						key: 'lastCommunicateTime',
						label: '通讯时间',
						value: this.detail.lastCommunicateTime,
					},
				].filter(m => !m.hidden),
			}
		},
	},
	methods: {
		displayIps(ips) {
			return Array.isArray(ips) ? ips.join('、') : ips
		},
		async handleDiagnosisEvent(device) {
			if (this.$route.name !== 'communicateDiagnosis') {
				this.$router.push({ name: 'communicateDiagnosis' })
			}
			try {
				await this.$store.dispatch('diagnosis/startDiagnosis', device)
			} catch (error) {
				this.$message.error(error.message)
			}
		},
	},
	mounted() {
		EventBus.$on('communicate-diagnosis', this.handleDiagnosisEvent)
	},
	beforeDestroy() {
		EventBus.$off('communicate-diagnosis', this.handleDiagnosisEvent)
	},
}
</script>
<style lang="scss" scoped>
.communicate-diagnosis {
	height: 100%;
	display: flex;
	flex-direction: column;
	.device-info {
		display: flex;
		flex-direction: column;
		.installed {
			background: #e4f5ec;
			color: #16a65d;
			&::before {
				position: absolute;
				top: 8px;
				left: 10px;
				display: block;
				content: '';
				width: 4px;
				height: 4px;
				border-radius: 50%;
				background-color: #16a65d;
			}
		}
	}
	.modal-container {
		flex: 1;
		height: 0;
		padding: 0 24px 12px;
		overflow-y: scroll;
		.diagnosis-top-row {
			display: flex;
			.el-col {
				display: flex;
			}
			::v-deep .diagnosis-analyze {
				width: 100%;
				padding-top: 20px;
			}
			::v-deep .device-basic-info {
				width: 100%;
			}
			&.compact {
				margin-top: 8px;
			}
		}
		.diagnosis-wait-panel,
		.diagnosis-error {
			margin-top: 16px;
			padding: 20px;
			border-radius: 4px;
			background: #f0f3fc;
			color: #5f667d;
			i {
				margin-right: 8px;
			}
		}
		.diagnosis-error {
			padding: 12px 16px;
			background: #e5662e1a;
			color: #e5662e;
		}
	}
}
::v-deep .gc-blank-page {
	margin-top: 15%;
}
</style>
